import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { Calendar, Tag, Handshake, LayoutDashboard, LogOut, Menu, X, ExternalLink, Users, ShieldCheck, ScrollText, BookOpen, KeyRound, Award } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import logoWhite from '@/imports/AlbaIuliaCTRo_logo_white.svg';

type NavAccess = 'all' | 'staff' | 'super' | 'partner';

const NAV_ITEMS: { label: string; path: string; icon: typeof LayoutDashboard; exact?: boolean; access: NavAccess }[] = [
  { label: 'Dashboard', path: '/staff', icon: LayoutDashboard, exact: true, access: 'all' },
  { label: 'Activități', path: '/staff/activitati', icon: Calendar, access: 'staff' },
  { label: 'Activitățile mele', path: '/staff/activitatile-mele', icon: Calendar, access: 'partner' },
  { label: 'Organizațiile mele', path: '/staff/organizatiile-mele', icon: Handshake, access: 'partner' },
  { label: 'Prieteni', path: '/staff/parteneri', icon: Handshake, access: 'staff' },
  { label: 'Parteneri & Sponsori', path: '/staff/parteneri-sponsori', icon: Award, access: 'super' },
  { label: 'Tag-uri', path: '/staff/taguri', icon: Tag, access: 'staff' },
  { label: 'Echipe', path: '/staff/echipe', icon: ShieldCheck, access: 'super' },
  { label: 'Utilizatori', path: '/staff/utilizatori', icon: Users, access: 'super' },
  { label: 'Jurnal', path: '/staff/jurnal', icon: ScrollText, access: 'super' },
  { label: 'Documentație', path: '/staff/documentatie', icon: BookOpen, access: 'all' },
];

function NavLink({ item, pathname, onClick }: {
  item: typeof NAV_ITEMS[number];
  pathname: string;
  onClick?: () => void;
}) {
  const isActive = item.exact
    ? pathname === item.path
    : pathname === item.path || pathname.startsWith(item.path + '/');
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
        isActive
          ? 'bg-white/15 text-white'
          : 'text-white/70 hover:bg-white/10 hover:text-white'
      }`}
    >
      <Icon className="h-5 w-5" />
      {item.label}
    </Link>
  );
}

export function StaffLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/staff/login');
  }

  return (
    <div className="min-h-screen bg-[#f8f8fa]">
      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-[var(--vivid-purple)] md:flex">
        <div className="flex h-16 items-center px-5">
          <Link to="/staff">
            <img src={logoWhite} alt="CTRo 2026" className="h-8" />
          </Link>
        </div>

        <nav className="flex flex-1 flex-col gap-1 px-3 py-4">
          {NAV_ITEMS.filter((item) => {
              if (item.access === 'all') return true;
              if (item.access === 'super') return user?.is_admin;
              if (item.access === 'staff') return user?.is_staff;
              if (item.access === 'partner') return user?.partner_ids && user.partner_ids.length > 0;
              return false;
            }).map((item) => (
            <NavLink key={item.path} item={item} pathname={location.pathname} />
          ))}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <ExternalLink className="h-5 w-5" />
            Vezi site public
          </a>
        </nav>

        <div className="border-t border-white/20 px-3 py-4">
          <div className="mb-3 px-3 text-xs text-white/50">
            {user?.username}
          </div>
          <Link
            to="/staff/schimba-parola"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <KeyRound className="h-5 w-5" />
            Schimbă parola
          </Link>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-5 w-5" />
            Deconectare
          </button>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between bg-[var(--vivid-purple)] px-4 md:hidden">
        <Link to="/staff">
          <img src={logoWhite} alt="CTRo 2026" className="h-7" />
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-white hover:bg-white/10"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-14 w-64 bg-[var(--vivid-purple)] p-3 shadow-xl">
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS.filter((item) => {
              if (item.access === 'all') return true;
              if (item.access === 'super') return user?.is_admin;
              if (item.access === 'staff') return user?.is_staff;
              if (item.access === 'partner') return user?.partner_ids && user.partner_ids.length > 0;
              return false;
            }).map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  pathname={location.pathname}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <ExternalLink className="h-5 w-5" />
                Vezi site public
              </a>
            </nav>
            <div className="mt-4 border-t border-white/20 pt-4">
              <div className="mb-2 px-3 text-xs text-white/50">
                {user?.username}
              </div>
              <Link
                to="/staff/schimba-parola"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <KeyRound className="h-5 w-5" />
                Schimbă parola
              </Link>
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <LogOut className="h-5 w-5" />
                Deconectare
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="md:ml-60">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
